// src/BureauVeritasPortal.jsx
import React, { useLayoutEffect, useRef, useState } from "react";
import { Card, CardHeader, Col, Container, Row } from "reactstrap";
import "./styles/BureauVeritasPortal.css";
import logo from "../../assets/images/bureau-veritas/coldblock-logo.webp";
import bvLogo from "../../assets/images/bureau-veritas/bureau_veritas_north_america_inc_logo-removebg-preview.png";
import {
  mergePortalConfig,
  sanitizePortalHtml,
} from "../../helpers/companyPortalConfig";
import CustomPageResourcesMount from "./CustomPageResourcesMount";

const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-envelope text-portal-blue" viewBox="0 0 16 16">
    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
  </svg>
);

const ContactItem = ({ contact }) => (
  <div className="contact-item mb-4">
    <div className="d-flex">
      <div className="me-3">
        <MailIcon />
      </div>
      <div>
        <a href={`mailto:${contact.email}`} className="text-portal-blue text-decoration-none">
          {contact.email}
        </a>
        {contact.name || contact.title ? (
          <div className="small text-dark mt-1">
            {[contact.name, contact.title].filter(Boolean).join(", ")}
          </div>
        ) : null}
        {contact.organization ? (
          <div className="small text-dark">{contact.organization}</div>
        ) : null}
      </div>
    </div>
  </div>
);

const HtmlBlock = ({ className, html, tag: Tag = "div" }) => (
  <Tag
    className={className}
    dangerouslySetInnerHTML={{ __html: sanitizePortalHtml(html) }}
  />
);

const getBackgroundColor = (value, fallback = "#f1f1f1") => (
  /^#?[0-9A-Fa-f]{6}$/.test(String(value || "").trim())
    ? (String(value).startsWith("#") ? String(value) : `#${String(value)}`)
    : fallback
);

const CustomPageHtml = ({ html, documentIds, showDownloadAll = false }) => {
  const hostRef = useRef(null);
  const [shell, setShell] = useState(null);

  useLayoutEffect(() => {
    const root = hostRef.current;
    if (!root) return;
    setShell(root.querySelector("main.page-shell") || null);
  }, [html]);

  return (
    <>
      <div
        className="portal-page"
        ref={hostRef}
        dangerouslySetInnerHTML={{ __html: String(html) }}
      />
      <CustomPageResourcesMount documentIds={documentIds} htmlHost={shell} showDownloadAll={showDownloadAll} />
    </>
  );
};

const BureauVeritasPortal = ({
  config,
  companyLogoUrl,
  companyName,
  portalCustomHtmlEnabled,
  portalCustomHtml,
  customPageHtml = "",
  customPageDocumentIds = [],
  customPageSlug = "",
}) => {
  if (customPageHtml && String(customPageHtml).trim()) {
    return (
      <CustomPageHtml
        html={customPageHtml}
        documentIds={customPageDocumentIds}
        showDownloadAll={String(customPageSlug || "").toLowerCase() === "brand-assets"}
      />
    );
  }

  if (portalCustomHtmlEnabled && portalCustomHtml && String(portalCustomHtml).trim()) {
    return (
      <div
        className="portal-page"
        dangerouslySetInnerHTML={{ __html: String(portalCustomHtml) }}
      />
    );
  }

  const portal = mergePortalConfig(config);
  const rightLogo = companyLogoUrl || bvLogo;
  const rightLogoAlt = companyName ? `${companyName} logo` : "Company logo";

  return (
    <div className="portal-page">
      <div className="brandbar py-3">
        <Container className="text-left">
          <Row className="align-items-center">
            <Col xs="6" className="d-flex">
              <img
                src={logo}
                alt="ColdBlock Technologies"
                className="brandbar-logo"
              />
            </Col>
            <Col xs="6" className="d-flex justify-content-end">
              <img
                src={rightLogo}
                alt={rightLogoAlt}
                className="brandbar-logo brandbar-logo--right"
              />
            </Col>
          </Row>
        </Container>
      </div>

      {portal.hero?.enabled !== false && (
        <Container className="text-left pt-4 pt-md-5">
          <h1 className="portal-title mb-3 text-center">{portal.hero.title}</h1>
          {portal.hero.introHtml ? <HtmlBlock className="portal-intro" html={portal.hero.introHtml} /> : null}
        </Container>
      )}

      {(portal.hero?.enabled !== false || portal.technology?.enabled !== false) && (
        <Container className="py-4 py-md-5">
          <Row className="gy-4 align-items-center">
            {portal.hero?.enabled !== false && portal.hero?.videoUrl ? (
              <Col lg="6">
                <div className="video-wrapper shadow-sm">
                  <iframe
                    src={portal.hero.videoUrl}
                    title="ColdBlock Sample Digestion"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </Col>
            ) : null}

            {portal.technology?.enabled !== false ? (
              <Col lg={portal.hero?.enabled !== false && portal.hero?.videoUrl ? "6" : "12"}>
                <h2 className="section-subheading mb-3 text-start">
                  {portal.technology.heading}
                </h2>
                {portal.technology.paragraphs?.map((paragraph, index) => (
                  <p key={`${paragraph}-${index}`} className={index === portal.technology.paragraphs.length - 1 ? "mb-0 text-start" : "mb-3 text-start"}>
                    {paragraph}
                  </p>
                ))}
              </Col>
            ) : null}
          </Row>
        </Container>
      )}

      {portal.highlightBand?.enabled !== false ? (
        <div
          className="portal-highlight-band py-4 py-md-5"
          style={{ backgroundColor: getBackgroundColor(portal.highlightBand.backgroundColor) }}
        >
          <Container>
            <Row className="align-items-center gy-4">
              <Col lg="3">
                <Card className="border-0 bg-transparent mb-0 shadow-none">
                  <CardHeader className="portal-highlight-band-header">
                    <HtmlBlock className="mb-0" html={portal.highlightBand.headerHtml} />
                  </CardHeader>
                </Card>
              </Col>
              <Col lg="9">
                {portal.highlightBand.bodyHtml ? (
                  <HtmlBlock className="portal-intro portal-highlight-band-body mb-0" html={portal.highlightBand.bodyHtml} />
                ) : null}
              </Col>
            </Row>
          </Container>
        </div>
      ) : null}

      {portal.quoteHighlights?.enabled !== false ? (
        <Container className="py-5">
          {portal.quoteHighlights.quoteHtml ? (
            <div className="portal-quote-block mb-5">
              <img
                src="/left-quote.png"
                alt=""
                aria-hidden="true"
                className="portal-quote-icon"
              />
              <HtmlBlock className="portal-quote-content" html={portal.quoteHighlights.quoteHtml} />
            </div>
          ) : null}

          <Row className="align-items-center gy-4">
            <Col lg={portal.quoteHighlights.imageUrl ? "6" : "12"}>
              {portal.quoteHighlights.bodyHtml ? (
                <HtmlBlock className="portal-intro mb-4" html={portal.quoteHighlights.bodyHtml} />
              ) : null}

              {portal.quoteHighlights.highlights?.length ? (
                <ul className="portal-highlight-list mb-0">
                  {portal.quoteHighlights.highlights.map((item, index) => (
                    <HtmlBlock
                      key={`${index}-${item}`}
                      className="portal-highlight-list-item"
                      html={item}
                      tag="li"
                    />
                  ))}
                </ul>
              ) : null}
            </Col>

            {portal.quoteHighlights.imageUrl ? (
              <Col lg="6">
                <img
                  src={portal.quoteHighlights.imageUrl}
                  alt={portal.quoteHighlights.imageAlt || "Portal feature"}
                  className="portal-feature-image"
                />
              </Col>
            ) : null}
          </Row>
        </Container>
      ) : null}

      {portal.lab?.enabled !== false ? (
        <div className="lab-background py-5">
          <Container>
            <h2 className="section-heading mb-4 text-center">{portal.lab.heading}</h2>
            <p className="portal-intro mb-5 text-left">{portal.lab.intro}</p>

            <Row className="justify-content-center mb-5">
              {portal.lab.cards?.map((card, index) => (
                <Col md="4" className={index < portal.lab.cards.length - 1 ? "mb-4 mb-md-0" : ""} key={`${card.label}-${index}`}>
                  <a
                    href={card.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="icon-box-link"
                  >
                    <div className="icon-box">
                      {card.imageUrl ? (
                        <img src={card.imageUrl} alt={card.imageAlt || card.label} className="mb-3 icon-img" />
                      ) : null}
                      <div className="btn-pill">{card.label} &gt;</div>
                    </div>
                  </a>
                </Col>
              ))}
            </Row>

            {portal.lab.contactIntro ? (
              <p className="portal-intro mb-4 w-full text-left">{portal.lab.contactIntro}</p>
            ) : null}

            {portal.lab.contacts?.map((contact, index) => (
              <p key={`${contact.email}-${index}`} className={index === portal.lab.contacts.length - 1 ? "mb-0 w-full" : "mb-4 w-full"}>
                <a href={`mailto:${contact.email}`} className="email-link">{contact.email}</a>
                {contact.name || contact.title ? ` - ${[contact.name, contact.title].filter(Boolean).join(", ")}` : ""}
              </p>
            ))}
          </Container>
        </div>
      ) : null}

      {portal.importantContacts?.enabled !== false ? (
        <Container className="py-5" id="contact-info" name="contact-info">
          <h2 className="section-heading text-center mb-5 px-4">{portal.importantContacts.heading}</h2>
          <Row className="justify-content-center">
            <Col md="5" className="mb-4 mb-md-0">
              <h4 className="mb-4 text-portal-blue">{portal.importantContacts.leftTitle}</h4>
              {portal.importantContacts.leftContacts?.map((contact, index) => (
                <ContactItem key={`${contact.email}-${index}`} contact={contact} />
              ))}
            </Col>

            <Col md="5">
              <h4 className="mb-4 text-portal-blue">{portal.importantContacts.rightTitle}</h4>
              {portal.importantContacts.rightContacts?.map((contact, index) => (
                <ContactItem key={`${contact.email}-${index}`} contact={contact} />
              ))}
            </Col>
          </Row>
        </Container>
      ) : null}

      {portal.support?.enabled !== false ? (
        <div className="bg-light-blue py-5" id="support-info">
          <Container>
            <Row className="justify-content-center">
              <Col md="5" className="mb-4 mb-md-0">
                <h3 className="section-subheading mb-3">{portal.support.resourcesHeading}</h3>
                {portal.support.resourcesBodyHtml ? (
                  <HtmlBlock className="text-dark" html={portal.support.resourcesBodyHtml} />
                ) : null}
              </Col>
              <Col md="5">
                <h3 className="section-subheading mb-3">{portal.support.supportHeading}</h3>
                {portal.support.supportItems?.map((item, index) => (
                  <HtmlBlock key={`${index}-${item}`} className="text-dark" html={item} />
                ))}
              </Col>
            </Row>
          </Container>
        </div>
      ) : null}

      {portal.linkColumns?.enabled !== false ? (
        <Container className="py-5">
          <h2 className="section-heading text-center mb-5 px-4">{portal.linkColumns.heading}</h2>
          <Row className="justify-content-center">
            <Col md="5" className="mb-4 mb-md-0">
              <div className="portal-link-list">
                {portal.linkColumns.leftLinks?.map((link, index) => (
                  <a
                    key={`${link.href}-${index}`}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="portal-link-item"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </Col>
            <Col md="5">
              <div className="portal-link-list">
                {portal.linkColumns.rightLinks?.map((link, index) => (
                  <a
                    key={`${link.href}-${index}`}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="portal-link-item"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      ) : null}
    </div>
  );
};

export default BureauVeritasPortal;
